import { formatMoney, currencySymbol } from "../../../lib/currency";
import { partition, sortBy } from "lodash-es";
import { getReactiveItems } from "./actions";

export const getters = {
  formattedTotalPrice(state) {
    return formatMoney(
      state.data.total_price || 0,
      `${currencySymbol()}{{ amount_no_trailing_zeroes }}`
    );
  },
  formattedTotalDiscount(state) {
    return formatMoney(
      state.data.total_discount || 0,
      `${currencySymbol()}{{ amount_no_trailing_zeroes }}`
    );
  },
  totalSavings(state, _getters, _rootState, rootGetters) {
    const { items } = state.data;
    const itemSavings = items.map((lineItem) => {
      const compareAtPrice =
        rootGetters["metafields/getVariantCompareAtPrice"](
          parseInt(lineItem.variant_id)
        ) || lineItem.original_price;

      const itemCompareAtPrice = compareAtPrice * lineItem.quantity;
      return itemCompareAtPrice - lineItem.final_line_price;
    });

    const savingsTotal = itemSavings.reduce((sum, saving) => {
      return sum + saving;
    }, 0);

    return savingsTotal;
  },
  getItemsByIds: (state) => (ids) => {
    if (!ids) return [];
    return state.data.items.filter((item) => {
      return ids.find((_id) => _id === item.id || _id === item.product_id);
    });
  },
  note(state) {
    return state.data.note;
  },
  sortedItems(state) {
    const { data } = state;
    if (!data) return;
    const cartItems = data.items || [];
    const reversedItems = cartItems.slice().reverse();
    const [items, reactiveItems] = partition(
      reversedItems,
      (item) => !item.properties._reactive
    );
    if (!items.length && reactiveItems.length) {
      return reactiveItems;
    }

    let remainingReactiveItems = [...reactiveItems];
    const allSortedItems = items.reduce((sorted, item) => {
      const _reactiveItems = getReactiveItems(item, remainingReactiveItems);
      if (_reactiveItems && _reactiveItems.length) {
        remainingReactiveItems = remainingReactiveItems.filter(
          (_remainingReactiveItem) => {
            return !_reactiveItems.find(
              (_reactiveItem) => _reactiveItem.id === _remainingReactiveItem.id
            );
          }
        );
        return [...sorted, item, ...reactiveItems];
      }

      return [...sorted, item];
    }, []);
    return allSortedItems;
  },
  getFormattedPrice:
    () =>
    (price, showCurrencySuffix = false) => {
      return `${currencySymbol()}${formatMoney(
        price,
        "{{ amount_no_trailing_zeroes }}",
        showCurrencySuffix
      )}`;
    },
  getProductQuantity: (state) => (productId) => {
    const items = state.data.items;
    if (!items || !items.length) return null;
    return items.reduce((totalQuantity, cartItem) => {
      if (productId === cartItem.product_id)
        return totalQuantity + cartItem.quantity;
      return totalQuantity;
    }, 0);
  },
  getVariantQuantity: (state) => (variantId) => {
    const items = state.data.items;
    return items.reduce((totalQuantity, cartItem) => {
      if (cartItem.id === variantId) return totalQuantity + cartItem.quantity;
      return totalQuantity;
    }, 0);
  },
  optionsScoreMap(state) {
    // Returns a map of all items in the cart attached with a score based on the existing options in the cart.
    // Some items are weighed higher than others
    const productScore = {
      "always-essential-cooking-pan": 1.1,
      "perfect-pot": 1.05,
    };

    const scoreMap = state.data.items.reduce(
      (variantTitles, item, currentIdx) => {
        if (item.variant_title && item.variant_title !== "Default Title") {
          const primaryOptionValue = item.options_with_values[0].value;
          const currentItemScore = variantTitles[primaryOptionValue]
            ? variantTitles[primaryOptionValue]
            : 0;
          variantTitles[primaryOptionValue] = {
            score:
              currentItemScore +
              item.quantity * (productScore[item.handle] || 1),
            index: currentIdx,
          };
        }
        return variantTitles;
      },
      {}
    );

    const itemsWithScore = Object.keys(scoreMap).map((optionTitle) => {
      return {
        name: cartVariantOptionMap[optionTitle] || optionTitle,
        score: scoreMap[optionTitle].score,
        index: scoreMap[optionTitle].index,
      };
    });

    const sortedVariantOptions = sortBy(itemsWithScore, [
      "score",
      "index",
    ]).reverse();
    return sortedVariantOptions;
  },
  productIds(state) {
    return state.data.items.map((item) => item.product_id);
  },
  variantIds(state) {
    return state.data.items.map((item) => item.id);
  },
};

export const cartVariantOptionMap = {
  Blanch: "Blue Salt",
};
